import { apiRequest } from '../client'

export interface AdminCustomer {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  birthDate: string | null
  emailVerified: boolean
  deactivated: boolean
  createdAt: string | null
}

export interface AdminCustomerDetail extends AdminCustomer {
  orderCount: number
  loyaltyPoints: number
}

export interface CustomersListResponse {
  items: Array<AdminCustomer>
  total: number
  page: number
  pageSize: number
}

export interface CustomersListParams {
  search?: string
  status?: 'active' | 'deactivated'
  page?: number
  pageSize?: number
  tenantId?: number | null
  /** Internal-store roster: restrict the page to customers scoped to this restaurant. */
  scopedToRestaurantId?: number | null
}

/**
 * One assignable INTERNAL store for a customer.
 *
 * The server returns every internal store of the tenant, granted or not, so the sheet renders
 * toggles straight from this list and never has to decide which stores are assignable.
 */
export interface CustomerStoreScope {
  restaurantId: number
  name: string
  granted: boolean
  grantedAt: string | null
}

function withTenantId(path: string, tenantId?: number | null) {
  if (!tenantId) return path
  const params = new URLSearchParams({ tenantId: tenantId.toString() })
  return `${path}?${params.toString()}`
}

export const customersApi = {
  list: (params?: CustomersListParams) => {
    const query = new URLSearchParams()
    if (params?.search) query.set('search', params.search)
    if (params?.status) query.set('status', params.status)
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.pageSize !== undefined)
      query.set('pageSize', params.pageSize.toString())
    if (params?.tenantId) query.set('tenantId', params.tenantId.toString())
    if (params?.scopedToRestaurantId)
      query.set('scopedToRestaurantId', params.scopedToRestaurantId.toString())
    const qs = query.toString()
    return apiRequest<CustomersListResponse>(
      `/api/v0/admin/customers${qs ? `?${qs}` : ''}`,
    )
  },

  get: (id: number, tenantId?: number | null) =>
    apiRequest<AdminCustomerDetail>(
      withTenantId(`/api/v0/admin/customers/${id}`, tenantId),
    ),

  deactivate: (id: number, tenantId?: number | null) =>
    apiRequest<{ ok: boolean }>(
      withTenantId(`/api/v0/admin/customers/${id}/deactivate`, tenantId),
      { method: 'POST' },
    ),

  reactivate: (id: number, tenantId?: number | null) =>
    apiRequest<{ ok: boolean }>(
      withTenantId(`/api/v0/admin/customers/${id}/reactivate`, tenantId),
      { method: 'POST' },
    ),

  resendVerification: (id: number, tenantId?: number | null) =>
    apiRequest<{ ok: boolean }>(
      withTenantId(
        `/api/v0/admin/customers/${id}/resend-verification`,
        tenantId,
      ),
      { method: 'POST' },
    ),

  listStoreScopes: (id: number, tenantId?: number | null) =>
    apiRequest<{ items: Array<CustomerStoreScope> }>(
      withTenantId(`/api/v0/admin/customers/${id}/store-scopes`, tenantId),
    ),

  // PUT and DELETE are idempotent server-side, so the toggle can fire either without first
  // checking what the customer currently holds.
  grantStoreScope: (
    id: number,
    restaurantId: number,
    tenantId?: number | null,
  ) =>
    apiRequest<{ ok: boolean }>(
      withTenantId(
        `/api/v0/admin/customers/${id}/store-scopes/${restaurantId}`,
        tenantId,
      ),
      { method: 'PUT' },
    ),

  revokeStoreScope: (
    id: number,
    restaurantId: number,
    tenantId?: number | null,
  ) =>
    apiRequest<{ ok: boolean }>(
      withTenantId(
        `/api/v0/admin/customers/${id}/store-scopes/${restaurantId}`,
        tenantId,
      ),
      { method: 'DELETE' },
    ),
}
